import { ElementRef, Injectable, linkedSignal, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EmojiSearch } from '@ctrl/ngx-emoji-mart';
import { EmojiData, EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { EmojiPickerData } from '@fi-shared/fi-emoji-button/fi-emoji-picker-data.enum';
import { isDefined } from '@fi-shared/utils';
import { isNullOrUndefined } from 'util';
import { IEmoji, IEmojiMartOverlayData, TEmojiSet } from '@fi-shared/fi-emoji-button/fi-emoji';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiEmojiMartOverlayComponent } from '@fi-shared/fi-emoji-mart-overlay/fi-emoji-mart-overlay.component';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { IEmojiViewData } from '@fi-shared/fi-likes/fi-likes.d';

@Injectable()
export class FiEmojiService {
    private readonly emojisEyes: string[] = [':', ';'];
    private readonly _updateEmojiOverlayPosition$ = new Subject<void>();
    private readonly _triggerInsertEmoji$: Subject<IEmoji> = new Subject<IEmoji>();
    private readonly _toggleEmojiOverlayVisibility$: Subject<IEmojiMartOverlayData> =
        new Subject<IEmojiMartOverlayData>();
    private readonly _emojiOverlayMobileSize: { height: string; width: string } = {
        height: '356px',
        width: '242px',
    };
    private readonly _emojiOverlayDesktopSize: { height: string; width: string } = {
        height: '356px',
        width: '350px',
    };
    private _emojiOverlayElementRef: ElementRef;
    private emojiMartOverlayResult: IFiDialogResult;

    readonly defaultEmojies: ReadonlyArray<IEmojiViewData> = [
        {
            id: 'hearts',
            set: 'apple',
            sheet: [57, 22],
            unified: '2665-FE0F',
        },
        {
            id: '+1',
            set: 'apple',
            sheet: [12, 50],
            unified: '1F44D',
        },
        {
            id: 'joy',
            set: 'apple',
            sheet: [32, 22],
            unified: '1F602',
        },
        {
            id: 'open_mouth',
            set: 'apple',
            sheet: [33, 6],
            unified: '1F62E',
        },
        {
            id: 'cry',
            set: 'apple',
            sheet: [32, 54],
            unified: '1F622',
        },
    ];

    constructor(
        private emojiSearch: EmojiSearch,
        private emojiService: EmojiService,
        private dialogsService: FiDialogsService,
        private detectDevicesService: DetectDevicesService,
    ) {}

    public openEmojiOverlay(): () => void {
        this.emojiMartOverlayResult = this.dialogsService.custom({
            contentComponentRef: FiEmojiMartOverlayComponent,
            hideFooter: true,
            hideHeader: true,
            hideDialogTopBorder: true,
            disableHeightOverflowCheck: true,
            config: {
                disableClose: true,
                closeOnNavigation: false,
                backdropClass: 'emoji-mart-overlay-backdrop',
                panelClass: 'emoji-mart-overlay-panel',
                maxHeight: this.detectDevicesService.isMobilePhone()
                    ? this._emojiOverlayMobileSize.height
                    : this._emojiOverlayDesktopSize.height,
                maxWidth: this.detectDevicesService.isMobilePhone()
                    ? this._emojiOverlayMobileSize.width
                    : this._emojiOverlayDesktopSize.width,
                minHeight: 'unset',
                minWidth: 'unset',
                autoFocus: false,
            },
        });
        return () => this.emojiMartOverlayResult.close();
    }

    public triggerInsertEmoji(emoji: IEmoji): void {
        this._triggerInsertEmoji$.next(emoji);
    }

    public onEmojiInsertion$(): Observable<IEmoji> {
        return this._triggerInsertEmoji$.asObservable();
    }

    public updateEmojiOverlayPosition(): void {
        this._updateEmojiOverlayPosition$.next();
    }

    public setEmojiOverlayVisibility(data: IEmojiMartOverlayData): void {
        this._toggleEmojiOverlayVisibility$.next(data);
    }

    public onEmojiOverlayVisibilityChange$(): Observable<IEmojiMartOverlayData> {
        return this._toggleEmojiOverlayVisibility$.asObservable();
    }

    public set emojiOverlayElementRef(element: ElementRef) {
        this._emojiOverlayElementRef = element;
    }

    public get emojiOverlayElementRef(): ElementRef {
        return this._emojiOverlayElementRef;
    }

    public onUpdateEmojiOverlayPosition$(): Observable<void> {
        return this._updateEmojiOverlayPosition$.asObservable();
    }

    parseEmojiToText(emojiImg: HTMLElement): string {
        const emojiUnified: string = emojiImg.getAttribute('data-emoji-unified');
        const currentEmojiKey: string = Object.keys(this.emojiSearch.emojisList).find((emojiKey: string) => {
            return this.emojiSearch.emojisList[emojiKey].unified === emojiUnified;
        });
        return this.emojiSearch.emojisList[currentEmojiKey].native;
    }

    public parseTextToEmoji(text: string): string {
        // parse text symbols that represent emojis, e.g. :) :( :D etc.
        const emojis: EmojiData[] = [];
        this.emojisEyes.forEach((emojisEye: string) => {
            this.emojiSearch.search(emojisEye).forEach((emoji: EmojiData) => emojis.push(emoji));
        });
        const textArray: string[] = text.split(' ');
        textArray.forEach((word: string, index: number) => {
            if (word.startsWith('<img src="data:image')) return;
            emojis.forEach((emoji: EmojiData) => {
                emoji.emoticons.forEach((emoticon: string) => {
                    if (word.startsWith('<img src="data:image') || word.length !== emoticon.length) return;

                    const emoticonRegExpStr: string = `(?!<img[^>]*?>)(${emoticon.replace(
                        /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
                        '\\$&',
                    )})(?![^<]*?>)`;
                    const emoticonRegExp: RegExp = new RegExp(emoticonRegExpStr, 'g');

                    const emojiElement: HTMLImageElement = this.getEmojiImgElement(emoji.sheet, emoji);
                    textArray[index] = word.replace(emoticonRegExp, emojiElement.outerHTML);
                    word = textArray[index];
                });
            });
        });
        text = textArray.join(' ');

        // parse native emojis, e.g. 😂 😊 😁
        const regexpEmojiPresentation: RegExp = /\p{Emoji_Presentation}/gu;
        const emojisInMessage: string[] = text.match(regexpEmojiPresentation);

        if (!isNullOrUndefined(emojisInMessage))
            emojisInMessage.forEach((nativeEmoji: string) => {
                const currentEmojiData: EmojiData = this.emojiService.emojis.find(
                    (emoji: EmojiData) => emoji.native === nativeEmoji,
                );
                if (isDefined(currentEmojiData)) {
                    const emojiElement: HTMLImageElement = this.getEmojiImgElement(
                        currentEmojiData.sheet,
                        currentEmojiData,
                    );
                    const nativeEmojiRegExp: RegExp = new RegExp(nativeEmoji, 'g');
                    text = text.replace(nativeEmojiRegExp, emojiElement.outerHTML);
                }
            });
        return text;
    }

    getEmojiByUnifiedCode(unifiedCode: string, fiEmojiSet: TEmojiSet, fiEmojiImageSize: number): HTMLImageElement {
        const img = document.createElement('img');

        img.src =
            `https://cdn.jsdelivr.net/npm/emoji-datasource-${fiEmojiSet}@14.0.0/` +
            `img/${fiEmojiSet}/64/${unifiedCode.toLowerCase()}.png`;

        img.width = img.height = fiEmojiImageSize;

        img.style.display = 'block';
        img.style.width = img.style.height = `${fiEmojiImageSize}px`;

        return img;
    }

    public getEmojiImgElement(sheet: [number, number], emoji: { unified?: string }): HTMLImageElement {
        const emojiElement: HTMLImageElement = document.createElement('img');
        const emojiStyles = this.emojiService.emojiSpriteStyles(
            sheet,
            EmojiPickerData.Set,
            EmojiPickerData.EmojiChatSize,
        );
        // create an image 1x1 and set sprite style.
        // editing message doesn't work with different tags (div, svg, span, p)
        emojiElement.setAttribute(
            'src',
            "data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 500 500\'%3e%3c/svg%3e",
        );
        Object.assign(emojiElement.style, emojiStyles);
        emojiElement.style.margin = '0 0 3px';
        emojiElement.style.verticalAlign = 'middle';
        if (emoji?.unified) {
            emojiElement.setAttribute('data-emoji-unified', emoji.unified);
        }
        return emojiElement;
    }
}
